import { AssignmentModel } from 'api/model';
import { useGoogleMapsContext } from 'components/map/GoogleMapsContext';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  min-height: 300px;
`;

const SWEDEN_CENTER = { lat: 62.5, lng: 16.5 };

const CURRENT_COLOR = '#999999';
const HOVERED_COLOR = '#EA580C';

const START_ICON = 'images/play-circle.svg';
const END_ICON = 'images/flag-checkered.svg';

function makeMarkerIcon(url: string): google.maps.Icon {
  return {
    url,
    scaledSize: new google.maps.Size(20, 20),
    anchor: new google.maps.Point(10, 10),
  };
}

function createDashedLine(
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
  color: string,
  map: google.maps.Map
): google.maps.Polyline {
  return new google.maps.Polyline({
    path: [start, end],
    strokeOpacity: 0,
    icons: [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          strokeColor: color,
          scale: 3,
        },
        offset: '0',
        repeat: '15px',
      },
    ],
    map,
  });
}

type LabelLine = { text: string; color: string };

function createMapLabel(
  position: google.maps.LatLng,
  lines: LabelLine[],
  map: google.maps.Map
): google.maps.OverlayView {
  const overlay = new google.maps.OverlayView();
  let div: HTMLDivElement | null = null;

  overlay.onAdd = function onAdd() {
    div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.background = 'white';
    div.style.borderRadius = '4px';
    div.style.padding = '3px 6px';
    div.style.fontSize = '11px';
    div.style.fontWeight = 'bold';
    div.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
    div.style.whiteSpace = 'nowrap';
    div.style.pointerEvents = 'none';
    div.style.lineHeight = '1.4';

    lines.forEach((line, i) => {
      const row = document.createElement('div');
      row.textContent = line.text;
      row.style.color = line.color;
      if (i > 0) {
        row.style.borderTop = '1px solid #eee';
        row.style.paddingTop = '2px';
        row.style.marginTop = '2px';
      }
      div!.appendChild(row);
    });

    this.getPanes()?.floatPane.appendChild(div);
  };

  overlay.draw = function draw() {
    if (!div) return;
    const proj = this.getProjection();
    const pos = proj.fromLatLngToDivPixel(position);
    if (pos) {
      div.style.left = `${pos.x}px`;
      div.style.top = `${pos.y - 15}px`;
      div.style.transform = 'translate(-50%, -100%)';
    }
  };

  overlay.onRemove = function onRemove() {
    div?.parentNode?.removeChild(div);
    div = null;
  };

  overlay.setMap(map);
  return overlay;
}

const POSITION_EPSILON = 0.0001; // ~11 meters

function samePosition(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): boolean {
  return (
    Math.abs(a.lat - b.lat) < POSITION_EPSILON &&
    Math.abs(a.lng - b.lng) < POSITION_EPSILON
  );
}

interface Props {
  currentAssignment: AssignmentModel;
  hoveredAssignment: AssignmentModel | null;
  hoveredRegistrationNumber: string;
}

const ReplacePlaceholderCarMap: React.FC<Props> = ({
  currentAssignment,
  hoveredAssignment,
  hoveredRegistrationNumber,
}) => {
  const { scriptLoaded } = useGoogleMapsContext();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const currentMarkersRef = useRef<google.maps.Marker[]>([]);
  const currentLineRef = useRef<google.maps.Polyline | null>(null);
  const hoveredMarkersRef = useRef<google.maps.Marker[]>([]);
  const hoveredLineRef = useRef<google.maps.Polyline | null>(null);
  const labelsRef = useRef<google.maps.OverlayView[]>([]);

  // Initialize the map
  useEffect(() => {
    if (scriptLoaded && mapRef.current && !mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 5,
        center: SWEDEN_CENTER,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: true,
        fullscreenControl: false,
        panControl: false,
        zoomControl: true,
        scrollwheel: true,
        gestureHandling: 'greedy',
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        clickableIcons: false,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });
    }
  }, [scriptLoaded]);

  const fitBoundsToMarkers = useCallback((markers: google.maps.Marker[]) => {
    const map = mapInstanceRef.current;
    if (!map || markers.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    markers.forEach((m) => {
      const pos = m.getPosition();
      if (pos) bounds.extend(pos);
    });
    setTimeout(() => {
      map.fitBounds(bounds, 40);
    }, 1);
  }, []);

  // Add/update current assignment markers (grey) + dashed line
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old
    currentMarkersRef.current.forEach((m) => m.setMap(null));
    currentMarkersRef.current = [];
    currentLineRef.current?.setMap(null);
    currentLineRef.current = null;

    const markers: google.maps.Marker[] = [];
    let startPos: google.maps.LatLngLiteral | null = null;
    let endPos: google.maps.LatLngLiteral | null = null;

    if (
      currentAssignment.fromLatitude != null &&
      currentAssignment.fromLongitude != null
    ) {
      startPos = {
        lat: currentAssignment.fromLatitude,
        lng: currentAssignment.fromLongitude,
      };
      markers.push(
        new google.maps.Marker({
          position: startPos,
          map,
          icon: makeMarkerIcon(START_ICON),
          title: 'Utfyllnad start',
          zIndex: 10,
        })
      );
    }

    if (
      currentAssignment.toLatitude != null &&
      currentAssignment.toLongitude != null
    ) {
      endPos = {
        lat: currentAssignment.toLatitude,
        lng: currentAssignment.toLongitude,
      };
      markers.push(
        new google.maps.Marker({
          position: endPos,
          map,
          icon: makeMarkerIcon(END_ICON),
          title: 'Utfyllnad slut',
          zIndex: 10,
        })
      );
    }

    if (startPos && endPos) {
      currentLineRef.current = createDashedLine(
        startPos,
        endPos,
        CURRENT_COLOR,
        map
      );
    }

    currentMarkersRef.current = markers;

    const allMarkers = [...markers, ...hoveredMarkersRef.current];
    if (allMarkers.length > 0) {
      fitBoundsToMarkers(allMarkers);
    }
  }, [currentAssignment, fitBoundsToMarkers]);

  // Add/update hovered assignment markers (orange) + dashed line
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old
    hoveredMarkersRef.current.forEach((m) => m.setMap(null));
    hoveredMarkersRef.current = [];
    hoveredLineRef.current?.setMap(null);
    hoveredLineRef.current = null;

    if (!hoveredAssignment) {
      if (currentMarkersRef.current.length > 0) {
        fitBoundsToMarkers(currentMarkersRef.current);
      }
      return;
    }

    const markers: google.maps.Marker[] = [];
    let startPos: google.maps.LatLngLiteral | null = null;
    let endPos: google.maps.LatLngLiteral | null = null;

    if (
      hoveredAssignment.fromLatitude != null &&
      hoveredAssignment.fromLongitude != null
    ) {
      startPos = {
        lat: hoveredAssignment.fromLatitude,
        lng: hoveredAssignment.fromLongitude,
      };
      markers.push(
        new google.maps.Marker({
          position: startPos,
          map,
          icon: makeMarkerIcon(START_ICON),
          title: 'Kandidat start',
          zIndex: 5,
        })
      );
    }

    if (
      hoveredAssignment.toLatitude != null &&
      hoveredAssignment.toLongitude != null
    ) {
      endPos = {
        lat: hoveredAssignment.toLatitude,
        lng: hoveredAssignment.toLongitude,
      };
      markers.push(
        new google.maps.Marker({
          position: endPos,
          map,
          icon: makeMarkerIcon(END_ICON),
          title: 'Kandidat slut',
          zIndex: 5,
        })
      );
    }

    if (startPos && endPos) {
      hoveredLineRef.current = createDashedLine(
        startPos,
        endPos,
        HOVERED_COLOR,
        map
      );
    }

    hoveredMarkersRef.current = markers;

    const allMarkers = [...currentMarkersRef.current, ...markers];
    if (allMarkers.length > 0) {
      fitBoundsToMarkers(allMarkers);
    }
  }, [hoveredAssignment, fitBoundsToMarkers]);

  // Render grouped labels as overlay boxes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear old labels
    labelsRef.current.forEach((l) => l.setMap(null));
    labelsRef.current = [];

    // Collect all label entries
    type LabelEntry = { lat: number; lng: number; text: string; color: string };
    const entries: LabelEntry[] = [];

    if (
      currentAssignment.fromLatitude != null &&
      currentAssignment.fromLongitude != null
    ) {
      entries.push({
        lat: currentAssignment.fromLatitude,
        lng: currentAssignment.fromLongitude,
        text: 'Utfyllnad start',
        color: CURRENT_COLOR,
      });
    }
    if (
      currentAssignment.toLatitude != null &&
      currentAssignment.toLongitude != null
    ) {
      entries.push({
        lat: currentAssignment.toLatitude,
        lng: currentAssignment.toLongitude,
        text: 'Utfyllnad slut',
        color: CURRENT_COLOR,
      });
    }

    if (hoveredAssignment) {
      const regLabel = hoveredRegistrationNumber || 'Kandidat';
      if (
        hoveredAssignment.fromLatitude != null &&
        hoveredAssignment.fromLongitude != null
      ) {
        entries.push({
          lat: hoveredAssignment.fromLatitude,
          lng: hoveredAssignment.fromLongitude,
          text: `${regLabel} start`,
          color: HOVERED_COLOR,
        });
      }
      if (
        hoveredAssignment.toLatitude != null &&
        hoveredAssignment.toLongitude != null
      ) {
        entries.push({
          lat: hoveredAssignment.toLatitude,
          lng: hoveredAssignment.toLongitude,
          text: `${regLabel} slut`,
          color: HOVERED_COLOR,
        });
      }
    }

    // Group entries by proximity
    const groups: {
      lat: number;
      lng: number;
      lines: LabelLine[];
    }[] = [];

    entries.forEach((entry) => {
      const existing = groups.find((g) =>
        samePosition(g, { lat: entry.lat, lng: entry.lng })
      );
      if (existing) {
        existing.lines.push({ text: entry.text, color: entry.color });
      } else {
        groups.push({
          lat: entry.lat,
          lng: entry.lng,
          lines: [{ text: entry.text, color: entry.color }],
        });
      }
    });

    // Create label overlays
    groups.forEach((group) => {
      const label = createMapLabel(
        new google.maps.LatLng(group.lat, group.lng),
        group.lines,
        map
      );
      labelsRef.current.push(label);
    });
  }, [currentAssignment, hoveredAssignment, hoveredRegistrationNumber]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      currentMarkersRef.current.forEach((m) => m.setMap(null));
      hoveredMarkersRef.current.forEach((m) => m.setMap(null));
      currentLineRef.current?.setMap(null);
      hoveredLineRef.current?.setMap(null);
      labelsRef.current.forEach((l) => l.setMap(null));
    };
  }, []);

  if (!scriptLoaded) {
    return <MapContainer>Laddar karta...</MapContainer>;
  }

  return <MapContainer ref={mapRef} />;
};

export default React.memo(ReplacePlaceholderCarMap);
